import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  AuthenticatedUserDto,
  EmployeeReadApiClient,
  People,
  fetchFromNewApi,
  fetchFromOldApi,
} from '~/services/Http';

export interface PermissionsState {
  enabledModules: string[];
  enabledTabs: { [module: string]: string[] };
}

export interface StoreState {
  userDetails?: AuthenticatedUserDto;
  employeeProfile?: People.EmployeeProfileDto;
  permissions?: PermissionsState;
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
}

const initialState: StoreState = {
  isLoading: false,
  failedToLoad: false,
  hasData: false,
};

export enum AuthActionKeyPrefixes {
  FETCH_AUTHENTICATED_USER_DETAILS = 'auth/FETCH_AUTHENTICATED_USER_DETAILS',
}

export interface AuthenticatedUserDetailsPayload {
  userDetails: AuthenticatedUserDto;
  employeeProfile: People.EmployeeProfileDto;
  enabledModules: string[];
  enabledTabs: { [module: string]: string[] };
}

export enum Modules {
  PEOPLE = 'PEOPLE',
  DEVELOPMENT = 'DEVELOPMENT',
  REWARDING = 'REWARDING',
  PERFORMANCE = 'PERFORMANCE',
}

export const getEnabledTabsForModule = (module: string): Promise<string[]> =>
  fetchFromOldApi(`/json/company/ENABLED_TABS_${module}/tabs`).then((r) =>
    r.json(),
  );

export const fetchAuthenticatedUserDetails = createAsyncThunk<
  AuthenticatedUserDetailsPayload,
  undefined
>(
  AuthActionKeyPrefixes.FETCH_AUTHENTICATED_USER_DETAILS,
  async (): Promise<AuthenticatedUserDetailsPayload> => {
    const userDetailsPromise = () =>
      fetchFromNewApi('/iam/auth/test').then((r) => r.json());

    const employeeProfilePromise = () =>
      EmployeeReadApiClient.getPublicProfile('me', undefined).then(
        (r) => r.data,
      );

    const enabledModulesPromise = () =>
      fetchFromOldApi('/json/module').then((r) => r.json());

    const enabledTabsPromise = () =>
      Promise.all([
        getEnabledTabsForModule(Modules.PEOPLE),
        getEnabledTabsForModule(Modules.DEVELOPMENT),
        getEnabledTabsForModule(Modules.REWARDING),
        getEnabledTabsForModule(Modules.PERFORMANCE),
      ]).then((values) => ({
        PEOPLE: values[0],
        DEVELOPMENT: values[1],
        REWARDING: values[2],
        PERFORMANCE: values[3],
      }));

    return Promise.all([
      userDetailsPromise(),
      employeeProfilePromise(),
      enabledModulesPromise(),
      enabledTabsPromise(),
    ]).then(
      ([
        userDetails,
        employeeProfile,
        enabledModules,
        enabledTabs,
      ]): AuthenticatedUserDetailsPayload => {
        return {
          userDetails,
          employeeProfile,
          enabledModules,
          enabledTabs,
        };
      },
    );
  },
);

export const authSlice = createSlice({
  name: '~/components/auth',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchAuthenticatedUserDetails.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<AuthenticatedUserDetailsPayload>,
    ) => {
      const { userDetails, enabledModules, employeeProfile, enabledTabs } =
        action.payload;

      state.userDetails = userDetails;
      state.employeeProfile = employeeProfile;
      state.permissions = {
        enabledModules,
        enabledTabs,
      };
      state.failedToLoad = false;
      state.isLoading = false;
      state.hasData =
        !!userDetails || !!enabledModules || !!employeeProfile || !!enabledTabs;
    },
    [fetchAuthenticatedUserDetails.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchAuthenticatedUserDetails.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const authReducer = authSlice.reducer;
